import * as React from "react";
import type { AgeBand, PortalRole } from "./institution";

/**
 * Session context + hook, split out from `session.tsx` so that file can
 * export only the `<SessionProvider>` component (react-refresh convention —
 * a fast-refresh-eligible module must export components only). Mirrors the
 * IA app's `AuthProvider` split.
 *
 * Phase SC-3b — real, JWT-backed identity. The access token lives in
 * memory inside the shared API client; the refresh token is the HttpOnly
 * `eos_refresh` cookie set by the BE on `/auth/login`. This context is
 * purely UI-facing state (never holds a token).
 */

export interface PortalUser {
  userId: string;
  name: string;
  avatarUrl?: string | null;
  role: PortalRole;
  ageBand: AgeBand;
  /** For guardians: the learners they can act for. */
  children: string[];
}

/** Credentials a learner/guardian types on the login form. */
export interface PortalLoginInput {
  email: string;
  password: string;
  /**
   * Explicit tenant slug — only sent when the host can't carry it (local
   * dev / bare `portal-stage` host, where the slug lives in the URL path).
   * On per-tenant hosts the API client injects `X-Tenant-Slug` and this is
   * omitted so a body value can never disagree with the subdomain.
   */
  tenantId?: string;
}

export interface SessionContextValue {
  user: PortalUser | null;
  /**
   * `loading` — booting: we're replaying the refresh cookie to see whether
   * a live session exists. `authenticated` / `anonymous` are terminal.
   */
  status: "loading" | "anonymous" | "authenticated";
  login: (input: PortalLoginInput) => Promise<void>;
  /**
   * Redeem a learner invite: sets the password, activates the account and
   * mounts the session (Phase SC-4c).
   *
   * Lives on the session rather than in the accept page because the BE
   * returns the same `{user, tokens}` shape as login and plants the same
   * refresh cookie — so the learner must end up signed in, not bounced
   * to a login form to retype the password they just chose.
   */
  acceptInvite: (input: { token: string; password: string }) => Promise<void>;
  /** Redeem a magic-link token and mount the session (Phase SC-6). */
  verifyMagicLink: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-fetch `/auth/me` after profile updates (avatar, etc.). */
  refreshProfile: () => Promise<void>;
}

export const SessionContext = React.createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const ctx = React.useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}
